// Settings → SyQi → Dashboard. The SyQi home is organized into five
// collapsible ORDER sections (Orient, Resources, Directions, Execute, Revise).
// This editor lets the user toggle which tools appear in each section.
//
// Persists to user_settings.syqi_widgets (jsonb key 'sections'), preserving
// other keys like the legacy 'widgets' array.
import React, { useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from '../../v10/theme';
import { Field, GlassCard, SectionTitle } from '../../v10/ui';
import {
  ORDER_SECTIONS,
  ORDER_SECTION_META,
  SECTION_TOOLS,
  useSyqiWidgets,
  enabledToolsFor,
  sectionLabel,
} from '../orderSections';

export default function SyqiDashboardEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { t } = useTheme();
  const { sections, setToolEnabled, overview, setOverview, loading } = useSyqiWidgets(userId);
  const overviewLoading = loading;
  const [openGear, setOpenGear] = useState<string | null>(null);

  const toggleSection = (gear: string) => {
    setOpenGear(openGear === gear ? null : gear);
  };

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <Text style={styles.caption}>
        Toggle the tools shown in each ORDER section of the SyQi dashboard. Section scores are computed from the active tools.
      </Text>

      {loading ? (
        <ActivityIndicator color={t.goldBright} style={{ marginTop: 20, marginBottom: 20 }} />
      ) : null}

      <GlassCard style={styles.overviewSection}>
        <SectionTitle>Overview</SectionTitle>
        <Text style={styles.overviewLabel}>
          Tell SyQi how your daily overview should work and what it should cover
        </Text>
        <Field
          inputStyle={styles.overviewInput}
          value={overview.prompt}
          onChangeText={(text) => setOverview({ prompt: text })}
          placeholder="e.g. Start with today's top priority, then flag anything overdue…"
          multiline
          textAlignVertical="top"
          editable={!overviewLoading}
        />
        {/* The old Text / Video / Voice mode pills are gone (2026-07-26) —
            one AI module everywhere, with video on/off in its ⋮ menu. */}
      </GlassCard>

      {ORDER_SECTIONS.map((gear) => {
        const meta = ORDER_SECTION_META[gear];
        const toolIds = SECTION_TOOLS[gear];
        const isOpen = openGear === gear;
        const enabled = enabledToolsFor(sections, gear);

        return (
          <GlassCard key={gear} style={styles.section}>
            {/* Section header */}
            <Pressable
              style={[styles.header, { borderLeftColor: meta.color }]}
              onPress={() => toggleSection(gear)}
            >
              <View style={styles.headerContent}>
                <Text style={[styles.headerTitle, { color: t.ivory }]}>{meta.title.toUpperCase()}</Text>
                <Text style={[styles.headerQuestion, { color: t.ivorySoft }]}>{meta.question}</Text>
              </View>
              <Text style={[styles.chevron, { color: t.goldBright }]}>{isOpen ? '▾' : '▸'}</Text>
            </Pressable>

            {/* Expanded section body */}
            {isOpen && (
              <View style={styles.body}>
                {toolIds.map((toolId) => {
                  const isEnabled = enabled.includes(toolId);
                  return (
                    <View key={toolId} style={styles.toolRow}>
                      <Text style={styles.toolName}>{sectionLabel(toolId)}</Text>
                      <ThemedSwitch
                        value={isEnabled}
                        onValueChange={(checked) => setToolEnabled(gear as any, toolId, checked)}
                      />
                    </View>
                  );
                })}
              </View>
            )}
          </GlassCard>
        );
      })}
    </ScrollView>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  content: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingBottom: 48,
    gap: 12,
  },
  caption: {
    fontSize: 12,
    color: t.ivorySoft,
    fontStyle: 'italic',
    lineHeight: 18,
    marginBottom: 4,
  },
  overviewSection: {
    padding: 14,
    gap: 8,
    marginBottom: 4,
  },
  overviewLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: t.ivory,
    marginBottom: 2,
  },
  overviewInput: {
    minHeight: 80,
  },
  section: {
    marginVertical: 4,
    padding: 0,
  },
  header: {
    borderLeftWidth: 3,
    borderLeftColor: 'transparent',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: '900',
    letterSpacing: 1,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  headerQuestion: {
    fontSize: 11,
    fontWeight: '600',
    marginTop: 2,
  },
  chevron: {
    fontSize: 16,
    fontWeight: '700',
    marginLeft: 8,
  },
  body: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 2,
    borderBottomLeftRadius: radii.tile,
    borderBottomRightRadius: radii.tile,
  },
  toolRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: radii.tile - 2,
  },
  toolName: {
    fontSize: 14,
    fontWeight: '600',
    color: t.ivory,
    flex: 1,
  },
}),
);
